import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Image, Video, Mic, Code, PenTool, Layout, Zap, Brain, Globe, ChevronDown } from 'lucide-react';
const categories = [{
  id: 'all',
  name: 'All Tools',
  icon: Globe
}, {
  id: 'Image',
  name: 'Image',
  icon: Image
}, {
  id: 'Video',
  name: 'Video',
  icon: Video
}, {
  id: 'Audio',
  name: 'Audio',
  icon: Mic
}, {
  id: 'Development',
  name: 'Development',
  icon: Code
}, {
  id: 'Writing',
  name: 'Writing',
  icon: PenTool
}, {
  id: 'Design',
  name: 'Design',
  icon: Layout
}, {
  id: 'Productivity',
  name: 'Productivity',
  icon: Zap
}, {
  id: 'Research',
  name: 'Research',
  icon: Brain
}];
interface CategoryNavProps {
  activeCategory: string;
  setActiveCategory: (category: string) => void;
  selectedCategories?: string[];
}
export function CategoryNav({
  activeCategory,
  setActiveCategory,
  selectedCategories = []
}: CategoryNavProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const activeItem = categories.find(cat => cat.id === activeCategory);
  const handleCategorySelect = (categoryId: string) => {
    setActiveCategory(categoryId);
    setIsDropdownOpen(false);
  };
  // Check if category is selected in filter sidebar
  const isCategoryFiltered = (categoryId: string) => {
    return categoryId !== 'all' && selectedCategories.includes(categoryId);
  };
  return <div className="w-full py-6" data-id="element-182">
      {/* Mobile Dropdown */}
      <div className="md:hidden" data-id="element-183">
        <div className="relative" data-id="element-184">
          <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="w-full flex items-center justify-between px-5 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all text-white" data-id="element-185">
            <span className="flex items-center gap-3" data-id="element-186">
              {activeItem && <activeItem.icon size={20} data-id="element-187" />}
              <span className="font-medium" data-id="element-188">{activeItem?.name}</span>
              {isCategoryFiltered(activeCategory) && <span className="w-2 h-2 rounded-full bg-green-400" data-id="element-189" />}
            </span>
            <motion.div animate={{
            rotate: isDropdownOpen ? 180 : 0
          }} transition={{
            duration: 0.2
          }} data-id="element-190">
              <ChevronDown size={20} data-id="element-191" />
            </motion.div>
          </button>

          <AnimatePresence data-id="element-192">
            {isDropdownOpen && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} className="absolute top-full left-0 right-0 mt-2 bg-black/95 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden z-50 shadow-2xl" data-id="element-193">
                <div className="max-h-[60vh] overflow-y-auto" data-id="element-194">
                  {categories.map(cat => {
                const isActive = activeCategory === cat.id;
                const isFiltered = isCategoryFiltered(cat.id);
                return <button key={cat.id} onClick={() => handleCategorySelect(cat.id)} className={`
                          w-full flex items-center gap-3 px-5 py-3 transition-all relative
                          ${isActive ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-white border-l-4 border-cyan-400' : 'text-gray-400 hover:bg-white/5 hover:text-white border-l-4 border-transparent'}
                        `} data-id="element-195">
                        <cat.icon size={20} className="flex-shrink-0" data-id="element-196" />
                        <span className="font-medium" data-id="element-197">{cat.name}</span>
                        {isFiltered && <span className="ml-auto w-2 h-2 rounded-full bg-green-400" data-id="element-198" />}
                      </button>;
              })}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Desktop Horizontal Scroll */}
      <div className="hidden md:block overflow-hidden" data-id="element-199">
        <div ref={scrollRef} className="flex gap-3 overflow-x-auto pb-2 px-4 md:px-0 scrollbar-hide snap-x snap-mandatory touch-pan-x" style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch'
      }} data-id="element-200">
          {categories.map((cat, index) => {
          const isActive = activeCategory === cat.id;
          const isFiltered = isCategoryFiltered(cat.id);
          return <motion.button key={cat.id} onClick={() => setActiveCategory(cat.id)} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: index * 0.05
          }} className={`
                  flex items-center justify-center gap-2 px-5 py-2.5 rounded-full whitespace-nowrap
                  border transition-all duration-300 relative overflow-hidden snap-center flex-shrink-0
                  ${isActive ? 'text-white font-bold border-transparent' : isFiltered ? 'bg-green-500/10 text-green-400 border-green-500/30 hover:bg-green-500/20' : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10 hover:border-white/20 hover:text-white'}
                `} data-id="element-201">
                {isActive && <motion.div layoutId="activeCategory" className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-purple-400" initial={false} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30
            }} data-id="element-202" />}
                <span className="relative z-10 flex items-center justify-center gap-2" data-id="element-203">
                  <cat.icon size={16} className="flex-shrink-0" data-id="element-204" />
                  <span className="text-sm font-medium" data-id="element-205">{cat.name}</span>
                  {isFiltered && !isActive && <span className="w-1.5 h-1.5 rounded-full bg-green-400" data-id="element-206" />}
                </span>
              </motion.button>;
        })}
        </div>
      </div>
    </div>;
}